
import { useContext, useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authApi, createAuthError } from '@/api/auth';
import { googleAuthService } from './google';
import { logSecurityEvent } from '@/utils/security';
import type { User, AuthError, GoogleCredentialResponse } from './types';
import { AuthContext } from './AuthProvider';

export const useAuth = () => {
  const context = useContext(AuthContext);
  
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};

export const useAuthQueries = () => {
  const queryClient = useQueryClient();

  // Check authentication status with enhanced error handling
  const {
    data: authData,
    isLoading: isLoadingAuth,
    error: authError,
  } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: authApi.checkAuth,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error) => {
      // Don't retry on 401 (unauthorized) or 403 (forbidden)
      if (error && 'statusCode' in error) {
        const statusCode = (error as any).statusCode;
        if (statusCode === 401 || statusCode === 403) {
          return false;
        }
      }

      // Log retry attempts for monitoring
      if (failureCount > 0) {
        logSecurityEvent('auth_check_retry', { 
          failureCount, 
          error: error instanceof Error ? error.message : 'Unknown' 
        });
      }

      return failureCount < 2;
    },
  });

  // Enhanced login mutation with security monitoring
  const loginMutation = useMutation({
    mutationFn: async (credential: string) => {
      logSecurityEvent('login_attempt');
      return await authApi.login(credential);
    },
    onSuccess: (data) => {
      // Update the auth cache
      queryClient.setQueryData(['auth', 'me'], data);
      
      // Invalidate and refetch related queries
      queryClient.invalidateQueries({ queryKey: ['auth'] });

      logSecurityEvent('login_mutation_success', { userId: data.user.id });
    },
    onError: (error) => {
      console.error('Login failed:', error);
      logSecurityEvent('login_mutation_error', { 
        error: error instanceof Error ? error.message : 'Unknown' 
      });
    },
  });

  // Enhanced logout mutation with proper cleanup
  const logoutMutation = useMutation({
    mutationFn: async () => {
      logSecurityEvent('logout_attempt');
      await authApi.logout();
    },
    onSuccess: () => {
      // Clear auth cache
      queryClient.setQueryData(['auth', 'me'], null);
      
      // Clear all cached data for security
      queryClient.clear();
      
      // Disable Google auto-select
      googleAuthService.disableAutoSelect();

      logSecurityEvent('logout_mutation_success');
    },
    onError: (error) => {
      console.error('Logout failed:', error);
      logSecurityEvent('logout_mutation_error', { 
        error: error instanceof Error ? error.message : 'Unknown' 
      });
    },
  });

  // Session refresh function for enhanced session management
  const refreshSession = useCallback(async () => {
    try {
      const result = await queryClient.fetchQuery({
        queryKey: ['auth', 'me'],
        queryFn: authApi.checkAuth,
        staleTime: 0, // Force fresh fetch
      });
      
      if (!result) {
        // Session expired, clear everything
        queryClient.setQueryData(['auth', 'me'], null);
        queryClient.clear();
        logSecurityEvent('session_expired');
      }
      
      return result;
    } catch (error) {
      logSecurityEvent('session_refresh_error', { 
        error: error instanceof Error ? error.message : 'Unknown' 
      });
      return null;
    }
  }, [queryClient]);

  return {
    // State
    user: authData?.user || null,
    isAuthenticated: !!authData?.user,
    isLoading: isLoadingAuth || loginMutation.isPending || logoutMutation.isPending,
    error: (authError || loginMutation.error || logoutMutation.error) 
      ? createAuthError(authError || loginMutation.error || logoutMutation.error)
      : null,

    // Actions
    login: loginMutation.mutateAsync,
    logout: logoutMutation.mutateAsync,
    refreshSession,
    
    // Mutation states
    isLoggingIn: loginMutation.isPending,
    isLoggingOut: logoutMutation.isPending,
    loginError: loginMutation.error ? createAuthError(loginMutation.error) : null,
    logoutError: logoutMutation.error ? createAuthError(logoutMutation.error) : null,
  };
};
